import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { Warning, Download, Trash } from '@phosphor-icons/react';

/**
 * DangerZone — GDPR/CCPA-compliant account ops on Profile.
 *   - Export all my data as a JSON file
 *   - Delete my account permanently (with confirmation phrase)
 */
const DangerZone = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get('/auth/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `bubblex_export_${user.username}_${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('your data has been exported');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'export failed');
    } finally { setExporting(false); }
  };

  const submitDelete = async () => {
    if (confirmText !== 'DELETE MY BUBBLE') {
      toast.error("type 'DELETE MY BUBBLE' exactly");
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/auth/account', { data: { confirm: confirmText, password } });
      toast.success('your account has been erased');
      logout();
      nav('/');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not delete');
    } finally { setDeleting(false); }
  };

  return (
    <div className="glass rounded-2xl p-5 mt-6 border border-bx-magenta/20" data-testid="danger-zone">
      <div className="flex items-center gap-2 mb-3">
        <Warning size={16} className="text-bx-magenta" weight="duotone" />
        <h3 className="font-display font-bold">Danger Zone</h3>
      </div>
      <p className="text-xs text-white/55 mb-4">
        Your data belongs to you. Take it with you anytime, or erase yourself completely.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={exportData} disabled={exporting}
          data-testid="export-data-btn"
          className="flex-1 px-4 py-2 rounded-full glass-hi hover:bg-white/10 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5 disabled:opacity-50">
          <Download size={12} weight="bold" /> {exporting ? 'exporting…' : 'export my data'}
        </button>
        <button onClick={() => setConfirmOpen(true)}
          data-testid="delete-account-btn"
          className="flex-1 px-4 py-2 rounded-full bg-bx-magenta/15 hover:bg-bx-magenta/25 text-bx-magenta text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-1.5">
          <Trash size={12} weight="bold" /> delete account
        </button>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[400] grid place-items-center bg-black/85 backdrop-blur px-4" onClick={() => setConfirmOpen(false)}>
          <div className="glass rounded-3xl p-6 w-full max-w-md border border-bx-magenta/40" onClick={(e) => e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest text-bx-magenta">irreversible</div>
            <div className="font-display text-2xl font-black mt-1">erase yourself</div>
            <p className="text-sm text-white/70 mt-3">
              All your bubbles, drifts, diaries, cosmetics, and your account will be permanently deleted.
              Comments on shared scenes will become "[redacted by user]". This cannot be undone.
            </p>
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-1">type exactly</div>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE MY BUBBLE"
                data-testid="delete-confirm-input"
                className="w-full glass-hi rounded-xl p-3 text-sm outline-none placeholder-white/30" />
              {user?.password_hash !== null && (
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  placeholder="your password (if you have one)"
                  data-testid="delete-confirm-password"
                  className="w-full glass-hi rounded-xl p-3 text-sm outline-none placeholder-white/30 mt-2" />
              )}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setConfirmOpen(false)}
                className="flex-1 px-4 py-2 rounded-full glass-hi text-xs uppercase tracking-widest font-bold">
                keep me
              </button>
              <button onClick={submitDelete} disabled={deleting || confirmText !== 'DELETE MY BUBBLE'}
                data-testid="delete-confirm-final"
                className="flex-1 px-4 py-2 rounded-full bg-bx-magenta text-black text-xs uppercase tracking-widest font-bold disabled:opacity-50">
                {deleting ? 'erasing…' : 'erase me'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DangerZone;
